/**
 * Overtime multiplier bands (§4.1).
 * Overtime and on-call on-site are split at each band change.
 * Payable hours use listBandSegments, not the highest sample.
 */

export const OT_BANDS = [
  {
    id: 'weekday_before_08',
    days: [1, 2, 3, 4, 5], // Mon–Fri
    startMinutes: 0,
    endMinutes: 8 * 60,
    multiplier: 1.5,
  },
  {
    id: 'weekday_after_17',
    days: [1, 2, 3, 4, 5],
    startMinutes: 17 * 60,
    endMinutes: 24 * 60,
    multiplier: 1.5,
  },
  {
    id: 'saturday_am',
    days: [6],
    startMinutes: 8 * 60,
    endMinutes: 12 * 60 + 30,
    multiplier: 1.5,
  },
  {
    id: 'saturday_pm',
    days: [6],
    startMinutes: 12 * 60 + 30,
    endMinutes: 24 * 60,
    multiplier: 2.0,
  },
  {
    id: 'sunday_all',
    days: [0],
    startMinutes: 0,
    endMinutes: 24 * 60,
    multiplier: 2.0,
  },
];

/** Parse "HH:mm" → minutes from midnight */
export const timeToMinutes = timeStr => {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = timeStr.split(':');
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

export const minutesToHours = minutes =>
  Math.round((minutes / 60) * 100) / 100;

/**
 * Duration in minutes between start/end.
 * Supports overnight rollover (end < start → +24h).
 */
export const durationMinutes = (startTime, endTime) => {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (start == null || end == null) return null;
  if (end === start) return 0;
  if (end > start) return end - start;
  return 24 * 60 - start + end;
};

const dayOfWeek = workDate =>
  // workDate YYYY-MM-DD — noon avoids DST edge cases
  new Date(`${workDate}T12:00:00`).getDay();

/**
 * Multiplier for a point-in-time on a given date (for on-site / OT bands).
 * Default 1.0 outside OT bands (e.g. Mon–Fri 08:00–17:00).
 */
export const multiplierAt = (workDate, timeStr) => {
  const minutes = timeToMinutes(timeStr);
  if (minutes == null || !workDate) return 1;
  const dow = dayOfWeek(workDate);
  const match = OT_BANDS.find(
    band =>
      band.days.includes(dow) &&
      minutes >= band.startMinutes &&
      minutes < band.endMinutes,
  );
  return match ? match.multiplier : 1;
};

const PREMIUM_CATEGORIES = ['overtime', 'on_call_onsite'];

// Not a band. Overtime and on-call on-site are extra by definition, so a
// clock time the band table does not cover is still paid above single time.
const PREMIUM_FLOOR = 1.5;

const premiumMultiplierAt = (workDate, timeStr) => {
  const band = multiplierAt(workDate, timeStr);
  return band > 1 ? band : PREMIUM_FLOOR;
};

/**
 * Dominant-band multiplier for a continuous shift (Phase 1).
 * Uses the higher multiplier across start and end samples, and mid-point.
 * On-call travel is always 1.5× regardless of day/time.
 */
export const getDominantMultiplier = ({
  workDate,
  startTime,
  endTime,
  category,
}) => {
  if (category === 'on_call_travel') {
    return 1.5;
  }

  if (!workDate || !startTime) {
    return 1;
  }

  const samples = [startTime];
  if (endTime) {
    samples.push(endTime);
    const start = timeToMinutes(startTime);
    const dur = durationMinutes(startTime, endTime);
    if (start != null && dur != null && dur > 0) {
      const mid = (start + Math.floor(dur / 2)) % (24 * 60);
      const hh = String(Math.floor(mid / 60)).padStart(2, '0');
      const mm = String(mid % 60).padStart(2, '0');
      samples.push(`${hh}:${mm}`);
    }
  }

  const rateAt = PREMIUM_CATEGORIES.includes(category)
    ? premiumMultiplierAt
    : multiplierAt;
  let max = 1;
  samples.forEach(t => {
    const m = rateAt(workDate, t);
    if (m > max) max = m;
  });

  // On-call on-site uses same day/time bands
  return max;
};

const addDaysISO = (dateStr, delta) => {
  const dt = new Date(`${dateStr}T12:00:00`);
  dt.setDate(dt.getDate() + delta);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const minutesToTime = total => {
  const mins = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const hh = String(Math.floor(mins / 60)).padStart(2, '0');
  const mm = String(mins % 60).padStart(2, '0');
  return `${hh}:${mm}`;
};

/** Overtime and on-call on-site. Everything else stays one rate. */
const BAND_SPLIT_CATEGORIES = ['overtime', 'on_call_onsite'];

/**
 * One segment per rate change.
 * Overtime and on-call on-site walk the bands, including across midnight.
 * On-call travel is one 1.5× segment. Normal travel and every other category
 * are one 1.0× segment. Overtime and on-call on-site use the band table, and
 * any gap the table does not cover uses the premium floor instead of 1×.
 */
export const listBandSegments = ({
  workDate,
  startTime,
  endTime,
  category,
}) => {
  const dur = durationMinutes(startTime, endTime);
  if (dur == null || dur <= 0) return [];

  if (category === 'on_call_travel') {
    return [
      {
        multiplier: 1.5,
        minutes: dur,
        hours: minutesToHours(dur),
        startTime,
        endTime,
      },
    ];
  }

  if (!BAND_SPLIT_CATEGORIES.includes(category)) {
    return [
      {
        multiplier: 1,
        minutes: dur,
        hours: minutesToHours(dur),
        startTime,
        endTime,
      },
    ];
  }

  const start = timeToMinutes(startTime);
  if (start == null || !workDate) return [];

  const pieces = [];
  let cursor = 0;
  while (cursor < dur) {
    const abs = start + cursor;
    const date = addDaysISO(workDate, Math.floor(abs / (24 * 60)));
    const multiplier = premiumMultiplierAt(date, minutesToTime(abs));
    let step = 1;
    while (cursor + step < dur) {
      const nextAbs = start + cursor + step;
      const nextDate = addDaysISO(workDate, Math.floor(nextAbs / (24 * 60)));
      const next = premiumMultiplierAt(nextDate, minutesToTime(nextAbs));
      if (next !== multiplier) break;
      step += 1;
    }
    pieces.push({ multiplier, minutes: step, startAbs: abs });
    cursor += step;
  }

  const segments = [];
  pieces.forEach(piece => {
    const prev = segments[segments.length - 1];
    const segmentEnd = minutesToTime(piece.startAbs + piece.minutes);
    if (prev && prev.multiplier === piece.multiplier) {
      prev.minutes += piece.minutes;
      prev.hours = minutesToHours(prev.minutes);
      prev.endTime = segmentEnd;
      return;
    }
    segments.push({
      multiplier: piece.multiplier,
      minutes: piece.minutes,
      hours: minutesToHours(piece.minutes),
      startTime: minutesToTime(piece.startAbs),
      endTime: segmentEnd,
    });
  });
  return segments;
};
